import React, {useEffect, useRef, useState, useCallback, useMemo} from "react"
import * as PlatformApi from '@dtplatform/platform-api'
import { IafScriptEngine } from '@dtplatform/iaf-script-engine'
import { IafDataSource } from '@dtplatform/platform-api'
import { ScriptHelper } from '@invicara/ipa-core/esm_modules/IpaUtils'
import { copyToClipboard } from "../utils/dev-mgmt-utils"

import './DeveloperOnboardingView.css'
import permissionsModule from "@/scripts/developer_onboarding_permissions.mjs";
import orchestratorModule from "@/scripts/developer_onboarding_orchestrator.mjs";
import * as workflowModule from "@/scripts/developer_onboarding_workflow.mjs";
import * as sharedModule from "@/scripts/developer_onboarding_shared.mjs";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {useApp} from "@/contexts/AppContext.tsx";
import {useConsoleAppAuth} from "@/hooks/useConsoleAppAuth.ts";

// Secrets collection configuration
const SECRETS_COLLECTION = {
  NAME: 'API Keys',
  SHORT_NAME: 'keys',
  USER_TYPE: 'keys',
  DESCRIPTION: 'Encrypted storage for API keys'
}

// Secret field names (prefixed with . for encryption)
const SECRET_FIELDS = {
  CLIENT_SECRET: '.clientSecret'
}

// Secret identifiers
const SECRET_NAMES = {
  SANDBOX: 'Sandbox',
  PROD: 'Prod'
}

const SANDBOX_API_URL = "https://sandbox-api.invicara.com"
const PROD_API_URL = "https://api.invicara.com"

/**
 * Get API URL based on handler config
 */
const getApiUrl = (handler) => {
    console.log('getApiUrl', handler);
    if (handler?.config?.host) {
        return handler.config.host
    }
    if (handler?.config?.env === 'Sandbox') {
        return SANDBOX_API_URL
    }
    return PROD_API_URL
}

const DeveloperOnboardingView = ({ handler }) => {
  // Tab state. Secrets is no longer a top-level tab — it has been demoted to
  // a collapsed "Advanced / Legacy" panel inside the Setup tab.
  const [activeTab, setActiveTab] = useState('onboard') // 'onboard' | 'setup'
  const [showHelp, setShowHelp] = useState(false)
  const [showHelpImageFullscreen, setShowHelpImageFullscreen] = useState(false)

  // Orchestrator state (legacy DataSource-based execution path)
  const [orchestrator, setOrchestrator] = useState(null)
  const [orchestratorLoading, setOrchestratorLoading] = useState(true)
  const [creatingOrchestrator, setCreatingOrchestrator] = useState(false)
  const [deletingOrchestrator, setDeletingOrchestrator] = useState(false)
  const [updatingProfile, setUpdatingProfile] = useState(false)

  // Workflow state (new WorkflowSvc-based execution path; mirrors orchestrator)
  const [workflow, setWorkflow] = useState(null)
  const [workflowLoading, setWorkflowLoading] = useState(true)
  const [creatingWorkflow, setCreatingWorkflow] = useState(false)
  const [deletingWorkflow, setDeletingWorkflow] = useState(false)
  const [updatingWorkflowProfile, setUpdatingWorkflowProfile] = useState(false)

  // Permission profile state
  const permissionProfileRequired = false
  const [permissionProfile, setPermissionProfile] = useState(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [creatingProfile, setCreatingProfile] = useState(false)
  const [deletingProfile, setDeletingProfile] = useState(false)

  // Secrets state
  const [sandboxClientSecret, setSandboxClientSecret] = useState('')
  const [existingSandboxSecret, setExistingSandboxSecret] = useState(null)
  const [prodClientSecret, setProdClientSecret] = useState('')
  const [existingProdSecret, setExistingProdSecret] = useState(null)
  const [secretsLoading, setSecretsLoading] = useState(false)
  const [savingSecrets, setSavingSecrets] = useState(false)

  // Organizations state (for dropdown)
  const [organizations, setOrganizations] = useState([])
  const [loadingOrganizations, setLoadingOrganizations] = useState(false)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('')
  const [createNewOrganization, setCreateNewOrganization] = useState(false)

  // Applications state (for dropdown)
  const [applications, setApplications] = useState([])
  const [loadingApplications, setLoadingApplications] = useState(false)
  const [selectedApplicationId, setSelectedApplicationId] = useState('')
  const [createNewApplication, setCreateNewApplication] = useState(false)

  // Form state - Organization
  const [organizationName, setOrganizationName] = useState('')
  const [organizationShortName, setOrganizationShortName] = useState('')
  const [newOrganizationId, setNewOrganizationId] = useState('') // Optional custom ID for new org

  // Form state - Application
  const [applicationName, setApplicationName] = useState('Twinit Academy')
  const [applicationShortName, setApplicationShortName] = useState('')
  const [newApplicationId, setNewApplicationId] = useState('') // Optional custom ID for new app

  // Form state - Users
  const [adminEmail, setAdminEmail] = useState('')
  const [developerEmails, setDeveloperEmails] = useState([])
  const [newDeveloperEmail, setNewDeveloperEmail] = useState('')

  // Form state - Options
  const [dryRun, setDryRun] = useState(false)


  // Form state - Skip flags
  const [skipRetrieveUser, setSkipRetrieveUser] = useState(false)
  const [skipEnsureOrganization, setSkipEnsureOrganization] = useState(false)
  const [skipSetOrgConfig, setSkipSetOrgConfig] = useState(false)
  const [skipEnsureApp, setSkipEnsureApp] = useState(false)
  const isSandbox = handler?.config?.env === 'Sandbox';
  const [skipAddToAppDevs, setSkipAddToAppDevs] = useState(false)
  const [skipAddToEnabledDevs, setSkipAddToEnabledDevs] = useState(isSandbox)

  const [isReady, setIsReady] = useState(false)
  // Per-service running flags so the two run buttons can be disabled
  // independently while either one is in flight.
  const [isRunningOrch, setIsRunningOrch] = useState(false)
  const [isRunningWorkflow, setIsRunningWorkflow] = useState(false)
  const isRunning = isRunningOrch || isRunningWorkflow

  // Results state
  const [successMessage, setSuccessMessage] = useState('')
  const [error, setError] = useState('')
  const [lastRunResult, setLastRunResult] = useState(null)

  // Recent runs state - orchestrator
  const [recentRuns, setRecentRuns] = useState([])
  const [loadingRuns, setLoadingRuns] = useState(false)
  const [runningCount, setRunningCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  // Recent runs state - workflow
  const [recentWorkflowRuns, setRecentWorkflowRuns] = useState([])
  const [loadingWorkflowRuns, setLoadingWorkflowRuns] = useState(false)
  const [runningWorkflowCount, setRunningWorkflowCount] = useState(0)
  const [workflowCurrentPage, setWorkflowCurrentPage] = useState(1)
  const runsPerPage = 5

  // Whether the legacy "Secrets" panel is expanded inside the Setup tab.
  // Hidden by default — only the orchestrator's two-tier auth fallback uses it
  // and the new workflow path does not need it at all.
  const [showLegacySecrets, setShowLegacySecrets] = useState(false)

  const scripts = useMemo(() => handler?.config?.scripts || {
      getDeveloperOnboardingPermissionProfile: "getDeveloperOnboardingPermissionProfile",
      createDeveloperOnboardingPermissionProfile: "createDeveloperOnboardingPermissionProfile",
      deleteDeveloperOnboardingPermissionProfile: "deleteDeveloperOnboardingPermissionProfile",
      // Orchestrator (legacy DataSource path)
      createDeveloperOnboardingOrchestrator: "createDeveloperOnboardingOrchestrator",
      updateOrchestratorPermissionProfile: "updateOrchestratorPermissionProfile",
      runDeveloperOnboardingOrchestrator: "runDeveloperOnboardingOrchestrator",
      // Workflow (new WorkflowSvc path)
      createDeveloperOnboardingWorkflow: "createDeveloperOnboardingWorkflow",
      updateWorkflowPermissionProfile: "updateWorkflowPermissionProfile",
      runDeveloperOnboardingWorkflow: "runDeveloperOnboardingWorkflow"
  }, [handler])

  // Get environment info for display
  const envInfo = useMemo(() => {
    const env = handler?.config?.env || 'Unknown'
    const host = handler?.config?.host || getApiUrl(handler)
    return { env, host }
  }, [handler])

  const { createCtx, accessToken, application: managementApp, user: currentUser } = useAuth();
  const { selectedProject, selectedApplication, applicationNamespaces } = useApp();
  const { consoleToken, isAuthenticating: isConsoleAuthenticating, error: consoleAuthError, authenticate: authenticateConsole, clearToken: clearConsoleToken, hasToken: hasConsoleToken, isTokenExpiring } = useConsoleAppAuth();

  // Get project namespace
  const getProjectNamespace = useCallback(() => {
    const namespaces = selectedProject?._namespaces || [];
    return namespaces.length > 0 ? [namespaces[0]] : [];
  }, [selectedProject]);

  const getCtx = useCallback(() => {
      const namespaces = getProjectNamespace();
      return {
        authToken: accessToken || '',
        _namespaces: namespaces,
        project: selectedProject,
      };
  }, [accessToken, getProjectNamespace, selectedProject]);

  // ============================================================================
  // ORGANIZATION & APPLICATION LOADING
  // ============================================================================

  const loadOrganizations = useCallback(async () => {
    setLoadingOrganizations(true)
    try {
      const apiUrl = getApiUrl(handler)
      const token = consoleToken || getCtx().authToken

      const response = await fetch(`${apiUrl}/passportsvc/api/v1/organizations?_pageSize=100`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })

      const result = await response.json()

      if (result._list) {
        setOrganizations(result._list)
      }
    } catch (err) {
      console.error('Failed to load organizations:', err)
    } finally {
      setLoadingOrganizations(false)
    }
  }, [handler, getCtx, consoleToken])

  const loadApplications = useCallback(async (orgId) => {
    if (!orgId) {
      setApplications([])
      return
    }

    setLoadingApplications(true)
    try {
      const apiUrl = getApiUrl(handler)
      const token = consoleToken || getCtx().authToken

      const response = await fetch(`${apiUrl}/passportsvc/api/v1/apps?_orgId=${orgId}&_pageSize=100`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })

      const result = await response.json()

      if (result._list) {
        setApplications(result._list)
      }
    } catch (err) {
      console.error('Failed to load applications:', err)
    } finally {
      setLoadingApplications(false)
    }
  }, [handler, getCtx, consoleToken])

  // ============================================================================
  // API CALLS
  // ============================================================================

  const loadPermissionProfile = useCallback(async () => {
    setProfileLoading(true)
    try {
      let result = await ScriptHelper.executeScript(
        scripts.getDeveloperOnboardingPermissionProfile,
        {}
      )
      console.log('Load permission profile result:', result)

      if (typeof result === 'string' && result.includes('not found')) {
          console.log('Load permission profile module result:', result)
          const ctx = getCtx();
          result = await permissionsModule.getDeveloperOnboardingPermissionProfile({}, {PlatformApi,IafScriptEngine}, ctx);
      }

      if (result?.exists) {
        setPermissionProfile(result.profile)
      } else {
        setPermissionProfile(null)
      }
    } catch (err) {
      console.error('Failed to load permission profile:', err)
      setPermissionProfile(null)
    } finally {
      setProfileLoading(false)
    }
  }, [scripts])

  const loadOrchestrator = useCallback(async () => {
    setOrchestratorLoading(true)
    try {
      const orchestrators = await IafDataSource.getOrchestratorBasedOnUserType('developer_onboarding')
      console.log('Load orchestrator result:', orchestrators);

      if (orchestrators?.length > 0) {
        setOrchestrator(orchestrators[0])
      } else {
        setOrchestrator(null)
      }
    } catch (err) {
      console.error('Failed to load orchestrator:', err)
      setOrchestrator(null)
    } finally {
      setOrchestratorLoading(false)
    }
  }, [])

  const createPermissionProfile = async () => {
    setCreatingProfile(true)
    setError('')
    setSuccessMessage('')

    try {
      let result = await ScriptHelper.executeScript(
        scripts.createDeveloperOnboardingPermissionProfile,
        {}
      )
      console.log('Create permission profile result:', result);

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx();
        result = await permissionsModule.createDeveloperOnboardingPermissionProfile({}, {PlatformApi,IafScriptEngine}, ctx);
        console.log('Create permission profile module result:', result);
      }

      setSuccessMessage(`Permission profile ${result.action} successfully!`)
      await loadPermissionProfile()
    } catch (err) {
      console.error('Failed to create permission profile:', err)
      setError(err.message || 'Failed to create permission profile')
    } finally {
      setCreatingProfile(false)
    }
  }

  const deletePermissionProfile = async () => {
    if (!window.confirm('Are you sure you want to delete the permission profile? This cannot be undone.')) {
      return
    }

    setDeletingProfile(true)
    setError('')
    setSuccessMessage('')

    try {
      let result = await ScriptHelper.executeScript(
        scripts.deleteDeveloperOnboardingPermissionProfile,
        {}
      )
      console.log('Delete permission profile result:', result)

      if (typeof result === 'string' && result.includes('not found')) {
          const ctx = getCtx();
          result = await permissionsModule.deleteDeveloperOnboardingPermissionProfile({}, {PlatformApi,IafScriptEngine}, ctx);
          console.log('Delete permission profile module result:', result)
      }

      if (result.deleted) {
        setSuccessMessage('Permission profile deleted successfully!')
        await loadPermissionProfile()
      } else {
        setError('Permission profile not found')
      }
    } catch (err) {
      console.error('Failed to delete permission profile:', err)
      setError(err.message || 'Failed to delete permission profile')
    } finally {
      setDeletingProfile(false)
    }
  }

  const createOrchestrator = async () => {
    setCreatingOrchestrator(true)
    setError('')
    setSuccessMessage('')

    try {
      const input = permissionProfile ? { permissionProfileId: permissionProfile._id } : {}

      let result = await ScriptHelper.executeScript(
        scripts.createDeveloperOnboardingOrchestrator,
        input
      )
      console.log('Create orchestrator result:', result)

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx();
        result = await orchestratorModule.createDeveloperOnboardingOrchestrator(input, {PlatformApi,IafScriptEngine}, ctx);
        console.log('Create orchestrator module result:', result)
      }

      setSuccessMessage('Developer Onboarding orchestrator created successfully!')
      await loadOrchestrator()
    } catch (err) {
      console.error('Failed to create orchestrator:', err)
      setError(err.message || 'Failed to create orchestrator')
    } finally {
      setCreatingOrchestrator(false)
    }
  }

  const updateOrchestratorProfile = async () => {
    if (!permissionProfile) {
      setError('Permission profile must exist before updating orchestrator')
      return
    }

    setUpdatingProfile(true)
    setError('')
    setSuccessMessage('')

    const input = { permissionProfileId: permissionProfile._id }

    try {
      let result = await ScriptHelper.executeScript(
        scripts.updateOrchestratorPermissionProfile,
        input
      )

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx();
        result = await orchestratorModule.updateOrchestratorPermissionProfile(input, {PlatformApi,IafScriptEngine}, ctx);
        console.log('Update orchestrator profile module result:', result)
      }

      if (result.success) {
        setSuccessMessage(result.message)
        await loadOrchestrator()
      } else {
        setError(result.error || 'Failed to update permission profile')
      }
    } catch (err) {
      console.error('Failed to update orchestrator profile:', err)
      setError(err.message || 'Failed to update orchestrator profile')
    } finally {
      setUpdatingProfile(false)
    }
  }

  const removeOrchestratorProfile = async () => {
    setUpdatingProfile(true)
    setError('')
    setSuccessMessage('')

    try {
      const input = { permissionProfileId: null }
      let result = await ScriptHelper.executeScript(
        scripts.updateOrchestratorPermissionProfile,
        input
      )

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx()
        result = await orchestratorModule.updateOrchestratorPermissionProfile(input, {PlatformApi, IafScriptEngine}, ctx)
      }

      if (result.success) {
        setSuccessMessage('Permission profile removed from orchestrator')
        await loadOrchestrator()
      } else {
        setError(result.error || 'Failed to remove permission profile')
      }
    } catch (err) {
      console.error('Failed to remove orchestrator profile:', err)
      setError(err.message || 'Failed to remove orchestrator profile')
    } finally {
      setUpdatingProfile(false)
    }
  }

  const deleteOrchestrator = async () => {
    if (!window.confirm('Are you sure you want to delete the orchestrator? This cannot be undone.')) {
      return
    }

    setDeletingOrchestrator(true)
    setError('')
    setSuccessMessage('')

    try {
      await IafDataSource.deleteOrchestrator(orchestrator.id)
      setSuccessMessage('Orchestrator deleted successfully!')
      await loadOrchestrator()
    } catch (err) {
      console.error('Failed to delete orchestrator:', err)
      setError(err.message || 'Failed to delete orchestrator')
    } finally {
      setDeletingOrchestrator(false)
    }
  }

  // ============================================================================
  // WORKFLOW (new WorkflowSvc path) - mirrors orchestrator handlers above
  // ============================================================================

  // Find the developer-onboarding WorkflowDef by _userType. The workflow
  // script tags every def it creates with _userType: "developer_onboarding_workflow".
  const loadWorkflow = useCallback(async () => {
    setWorkflowLoading(true)
    try {
      const { IafWorkflowSvc } = PlatformApi
      const ctx = getCtx()
      const resp = await IafWorkflowSvc.getWorkflowDefs({
        query: { _userType: 'developer_onboarding_workflow' }
      }, ctx)
      const list = resp?._list || []
      setWorkflow(list.length > 0 ? list[0] : null)
    } catch (err) {
      console.error('Failed to load workflow:', err)
      setWorkflow(null)
    } finally {
      setWorkflowLoading(false)
    }
  }, [getCtx])

  const createWorkflow = async () => {
    setCreatingWorkflow(true)
    setError('')
    setSuccessMessage('')

    try {
      const input = permissionProfile ? { permissionProfileId: permissionProfile._id } : {}

      let result = await ScriptHelper.executeScript(
        scripts.createDeveloperOnboardingWorkflow,
        input
      )
      console.log('Create workflow result:', result)

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx()
        result = await workflowModule.createDeveloperOnboardingWorkflow(input, {PlatformApi, IafScriptEngine}, ctx)
        console.log('Create workflow module result:', result)
      }

      setSuccessMessage('Developer Onboarding workflow created successfully!')
      await loadWorkflow()
    } catch (err) {
      console.error('Failed to create workflow:', err)
      setError(err.message || 'Failed to create workflow')
    } finally {
      setCreatingWorkflow(false)
    }
  }

  const updateWorkflowProfile = async () => {
    if (!permissionProfile) {
      setError('Permission profile must exist before updating workflow')
      return
    }

    setUpdatingWorkflowProfile(true)
    setError('')
    setSuccessMessage('')

    try {
      let result = await ScriptHelper.executeScript(
        scripts.updateWorkflowPermissionProfile,
        { permissionProfileId: permissionProfile._id }
      )

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx()
        result = await workflowModule.updateWorkflowPermissionProfile(
          { permissionProfileId: permissionProfile._id },
          {PlatformApi, IafScriptEngine},
          ctx
        )
      }

      if (result?.success) {
        setSuccessMessage(result.message)
        await loadWorkflow()
      } else {
        setError(result?.error || 'Failed to update workflow permission profile')
      }
    } catch (err) {
      console.error('Failed to update workflow profile:', err)
      setError(err.message || 'Failed to update workflow profile')
    } finally {
      setUpdatingWorkflowProfile(false)
    }
  }

  const removeWorkflowProfile = async () => {
    setUpdatingWorkflowProfile(true)
    setError('')
    setSuccessMessage('')

    try {
      let result = await ScriptHelper.executeScript(
        scripts.updateWorkflowPermissionProfile,
        { permissionProfileId: null }
      )

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx()
        result = await workflowModule.updateWorkflowPermissionProfile(
          { permissionProfileId: null },
          {PlatformApi, IafScriptEngine},
          ctx
        )
      }

      if (result?.success) {
        setSuccessMessage('Permission profile removed from workflow')
        await loadWorkflow()
      } else {
        setError(result?.error || 'Failed to remove workflow permission profile')
      }
    } catch (err) {
      console.error('Failed to remove workflow profile:', err)
      setError(err.message || 'Failed to remove workflow profile')
    } finally {
      setUpdatingWorkflowProfile(false)
    }
  }

  const deleteWorkflow = async () => {
    if (!window.confirm('Are you sure you want to delete the workflow? This cannot be undone.')) {
      return
    }

    setDeletingWorkflow(true)
    setError('')
    setSuccessMessage('')

    try {
      const { IafWorkflowSvc } = PlatformApi
      const ctx = getCtx()
      await IafWorkflowSvc.deleteWorkflowDef(workflow._id, ctx)
      setSuccessMessage('Workflow deleted successfully!')
      await loadWorkflow()
    } catch (err) {
      console.error('Failed to delete workflow:', err)
      setError(err.message || 'Failed to delete workflow')
    } finally {
      setDeletingWorkflow(false)
    }
  }

  // ============================================================================
  // SECRETS MANAGEMENT
  // ============================================================================

  const loadSecrets = useCallback(async () => {
    setSecretsLoading(true)
    try {
      const { IafItemSvc } = PlatformApi
      const ctx = getCtx()

      let collectionResp = await IafItemSvc.getNamedUserItems({
        query: { _userType: SECRETS_COLLECTION.USER_TYPE }
      }, ctx)

      if (!collectionResp?._list?.[0]) {
        setExistingSandboxSecret(null)
        setExistingProdSecret(null)
        return
      }

      const collection = collectionResp._list[0]

      const secretsResp = await IafItemSvc.getRelatedItems(
        collection._userItemId,
        {},
        ctx,
        { page: { _pageSize: 100 } }
      )

      const secrets = secretsResp._list || []

      const sandboxSecret = secrets.find(s => s._name === SECRET_NAMES.SANDBOX)
      const prodSecret = secrets.find(s => s._name === SECRET_NAMES.PROD)

      setExistingSandboxSecret(sandboxSecret || null)
      setExistingProdSecret(prodSecret || null)

      setSandboxClientSecret('')
      setProdClientSecret('')
    } catch (err) {
      console.error('Failed to load secrets:', err)
      setError('Failed to load secrets: ' + err.message)
    } finally {
      setSecretsLoading(false)
    }
  }, [getCtx])

  const saveSecrets = async () => {
    setSavingSecrets(true)
    setError('')
    setSuccessMessage('')

    try {
      const { IafItemSvc } = PlatformApi
      const ctx = getCtx()

      let collectionResp = await IafItemSvc.getNamedUserItems({
        query: { _userType: SECRETS_COLLECTION.USER_TYPE }
      }, ctx)

      let collection
      if (!collectionResp?._list?.[0]) {
        const newCollection = await IafItemSvc.createNamedUserItems([{
          _name: SECRETS_COLLECTION.NAME,
          _shortName: SECRETS_COLLECTION.SHORT_NAME,
          _description: SECRETS_COLLECTION.DESCRIPTION,
          _userType: SECRETS_COLLECTION.USER_TYPE,
          _namespaces: ctx._namespaces,
          _encryptionEnabled: true
        }], 'NamedUserCollection', ctx)
        collection = newCollection._list[0]
      } else {
        collection = collectionResp._list[0]
      }

      let updatedCount = 0
      const secretsToCreate = []

      if (sandboxClientSecret.trim()) {
        if (existingSandboxSecret) {
          await IafItemSvc.updateRelatedItem(
            collection._userItemId,
            existingSandboxSecret._id,
            {
              ...existingSandboxSecret,
              [SECRET_FIELDS.CLIENT_SECRET]: sandboxClientSecret.trim()
            },
            ctx
          )
        } else {
          secretsToCreate.push({
            _name: SECRET_NAMES.SANDBOX,
            [SECRET_FIELDS.CLIENT_SECRET]: sandboxClientSecret.trim()
          })
        }
        updatedCount++
      }

      if (prodClientSecret.trim()) {
        if (existingProdSecret) {
          await IafItemSvc.updateRelatedItem(
            collection._userItemId,
            existingProdSecret._id,
            {
              ...existingProdSecret,
              [SECRET_FIELDS.CLIENT_SECRET]: prodClientSecret.trim()
            },
            ctx
          )
        } else {
          secretsToCreate.push({
            _name: SECRET_NAMES.PROD,
            [SECRET_FIELDS.CLIENT_SECRET]: prodClientSecret.trim()
          })
        }
        updatedCount++
      }

      if (secretsToCreate.length > 0) {
        await IafItemSvc.createRelatedItems(
          collection._userItemId,
          secretsToCreate,
          ctx
        )
      }

      if (updatedCount > 0) {
        setSuccessMessage(`Saved ${updatedCount} secret${updatedCount > 1 ? 's' : ''} successfully!`)
        setSandboxClientSecret('')
        setProdClientSecret('')
        await loadSecrets()
      } else {
        setError('No secrets to save. Please enter values.')
      }
    } catch (err) {
      console.error('Failed to save secrets:', err)
      setError('Failed to save secrets: ' + err.message)
    } finally {
      setSavingSecrets(false)
    }
  }

  const deleteSecret = async (secretName) => {
    if (!window.confirm(`Are you sure you want to delete the ${secretName} secret? This cannot be undone.`)) {
      return
    }

    try {
      const { IafItemSvc } = PlatformApi
      const ctx = getCtx()

      let collectionResp = await IafItemSvc.getNamedUserItems({
        query: { _userType: SECRETS_COLLECTION.USER_TYPE }
      }, ctx)

      if (!collectionResp?._list?.[0]) {
        setError('Secrets collection not found')
        return
      }

      const collection = collectionResp._list[0]

      let secret = null
      switch (secretName) {
        case SECRET_NAMES.SANDBOX:
          secret = existingSandboxSecret
          break
        case SECRET_NAMES.PROD:
          secret = existingProdSecret
          break
      }

      if (secret) {
        await IafItemSvc.deleteRelatedItem(collection._userItemId, secret._id, ctx)
        setSuccessMessage(`${secretName} secret deleted successfully!`)
        await loadSecrets()
      }
    } catch (err) {
      console.error('Failed to delete secret:', err)
      setError('Failed to delete secret: ' + err.message)
    }
  }

  const deleteOrchestratorRun = async (runId) => {
    if (!window.confirm('Are you sure you want to delete this orchestrator run? This cannot be undone.')) {
      return
    }

    try {
      const ctx = getCtx()
      const namespace = getProjectNamespace();
      const apiUrl = getApiUrl(handler)
      const result = await fetch(
          `${apiUrl}/datasourcesvc/api/v1/orchrun/${runId}?nsfilter=${namespace}`,
         {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${ctx.authToken}`,
              }
          }
      );
      const deleteResult = result.json();
      console.log('Orchestrator run deleted successfully',deleteResult);
      setSuccessMessage('Orchestrator run deleted successfully')
      await loadRecentRuns()
    } catch (err) {
      console.error('Failed to delete orchestrator run:', err)
      setError('Failed to delete orchestrator run: ' + err.message)
    }
  }

  const getStepsSummary = (run) => {
    if (!run.orchrunsteps || run.orchrunsteps.length === 0) return '-'

    const statusCounts = run.orchrunsteps.reduce((acc, step) => {
      acc[step._status] = (acc[step._status] || 0) + 1
      return acc
    }, {})

    const parts = []
    if (statusCounts.COMPLETED) parts.push(`✓ ${statusCounts.COMPLETED}`)
    if (statusCounts.ERROR) parts.push(`✗ ${statusCounts.ERROR}`)
    if (statusCounts.RUNNING) parts.push(`⟳ ${statusCounts.RUNNING}`)
    if (statusCounts.QUEUED) parts.push(`○ ${statusCounts.QUEUED}`)

    return parts.join(' | ')
  }

  const viewRunDetails = (run) => {
    console.log('=== ORCHESTRATOR RUN DETAILS ===')
    console.log('Run ID:', run.id)
    console.log('Status:', run._status)
    console.log('Created:', new Date(parseInt(run._createdat)))
    console.log('\nSteps Status:')

    const sortedSteps = [...(run.orchrunsteps || [])].sort((a, b) => a._sequenceno - b._sequenceno)
    sortedSteps.forEach(step => {
      const statusIcon = step._status === 'COMPLETED' ? '✓' :
                        step._status === 'ERROR' ? '✗' :
                        step._status === 'RUNNING' ? '⟳' : '○'
      console.log(`  ${statusIcon} [${step._sequenceno}] ${step._name}: ${step._status}`)
      if (step._statusmsg) {
        console.log(`     Message: ${step._statusmsg}`)
      }
    })

    console.log('\nFull Run Object:')
    console.log(run)
    console.log('=================================')

    setSuccessMessage('Run details logged to console (F12)')
  }

  const loadRecentRuns = useCallback(async () => {
    if (!orchestrator) return

    setLoadingRuns(true)
    try {
      const runs = await IafDataSource.getOrchestratorRuns(null, {
        _orchid: orchestrator.id,
        _pageSize: 5,
        sort: {"sortkey":"_updatedat","sortby":"desc"}
      })

      if (runs?._list) {
        setRecentRuns(runs._list)
        const running = runs._list.filter(run =>
          run._status === 'running' || run._status === 'pending'
        ).length
        setRunningCount(running)
      }
    } catch (err) {
      console.error('Failed to load recent runs:', err)
    } finally {
      setLoadingRuns(false)
    }
  }, [orchestrator])

  // Workflow runs come from the WorkflowSvc REST endpoint
  // GET /workflowsvc/api/v1/workflows?_defId=<id>. The JS SDK exposes this as
  // IafWorkflowSvc.getWorkflows(criteria, ctx, options) — see Twinit JS docs.
  // Note: workflow runs use camelCase _createdAt/_updatedAt and uppercase
  // status values (QUEUED/RUNNING/COMPLETED/FAILED), unlike orchestrator runs
  // which use lowercase _createdat/_updatedat and lowercase statuses.
  const loadRecentWorkflowRuns = useCallback(async () => {
    if (!workflow) return

    setLoadingWorkflowRuns(true)
    try {
      const { IafWorkflowSvc } = PlatformApi
      const ctx = getCtx()
      // Fetch all runs sorted newest-first using server-side sort + pagination.
      const apiUrl = getApiUrl(handler)
      const namespace = getProjectNamespace()
      const sortParam = encodeURIComponent(JSON.stringify({ _createdAt: -1 }))
      const pageSize = 100
      let offset = 0
      let list = []
      while (true) {
        const resp = await fetch(
          `${apiUrl}/workflowsvc/api/v1/workflows?_defId=${workflow._id}&nsfilter=${namespace}&_pageSize=${pageSize}&_offset=${offset}&sort=${sortParam}`,
          { headers: { 'Authorization': `Bearer ${ctx.authToken}`, 'Content-Type': 'application/json' } }
        )
        const data = await resp.json()
        const page = data?._list || (Array.isArray(data) ? data : [])
        list = list.concat(page)
        if (page.length < pageSize) break
        offset += pageSize
      }
      setRecentWorkflowRuns(list)
      const running = list.filter(run => {
        const s = (run._status || '').toUpperCase()
        return s === 'RUNNING' || s === 'QUEUED' || s === 'PAUSED'
      }).length
      setRunningWorkflowCount(running)
    } catch (err) {
      console.error('Failed to load recent workflow runs:', err)
    } finally {
      setLoadingWorkflowRuns(false)
    }
  }, [workflow, getCtx])

  const deleteWorkflowRun = async (runId) => {
    // The Twinit Workflow Service does not support deleting individual run records.
    setError('Workflow run records cannot be deleted — the Twinit Workflow Service does not expose a delete endpoint for run history.')
  }

  const viewWorkflowRunDetails = async (run) => {
    const runId = run._id || run.id
    console.log('=== WORKFLOW RUN DETAILS ===')
    console.log('Run ID:', runId)
    console.log('Status:', run._status)
    console.log('Created:', run._createdAt ? new Date(run._createdAt) : '-')
    console.log('\nFull Run Object:')
    const redactedRun = run._inputParams?.consoleAuthToken
      ? { ...run, _inputParams: { ...run._inputParams, consoleAuthToken: '[REDACTED]' } }
      : run
    console.log(redactedRun)

    const tasks = Array.isArray(run._tasks) ? run._tasks : []
    if (!tasks.length) {
      console.log('\n(No tasks on this run)')
      console.log('=================================')
      setSuccessMessage('Workflow run details logged to console (F12)')
      return
    }

    const { IafWorkflowSvc } = PlatformApi
    const ctx = getCtx()

    console.log(`\n=== TASK DETAILS & LOGS (${tasks.length} tasks) ===`)

    // Dump tasks in workflow order so the console output is easy to read
    const ordered = [...tasks].sort((a, b) => {
      const sa = a._sequenceno ?? 0
      const sb = b._sequenceno ?? 0
      if (sa !== sb) return sa - sb
      // Stable secondary sort by id for duplicated-sequence tasks
      return String(a._id).localeCompare(String(b._id))
    })

    for (const task of ordered) {
      const taskId = task._id
      const label = `[seq ${task._sequenceno}] ${task._name} (${task._status}) ${taskId}`
      console.groupCollapsed(`Task ${label}`)
      try {
        const fullTask = await IafWorkflowSvc.getTaskById(runId, taskId, ctx)
        const redactedTask = fullTask?._inputParams?.consoleAuthToken
          ? { ...fullTask, _inputParams: { ...fullTask._inputParams, consoleAuthToken: '[REDACTED]' } }
          : fullTask
        console.log('Task:', redactedTask)
        if (redactedTask?._inputParams) {
          console.log('Input params:', redactedTask._inputParams)
        }
        if (fullTask?._output) {
          console.log('Output:', fullTask._output)
        }
      } catch (err) {
        console.warn('Failed to fetch task by id:', err?.errorResult?.errorMessage || err?.message || err)
      }

      try {
        const logs = await IafWorkflowSvc.getTaskLogsById(
          runId,
          taskId,
          ctx,
          undefined,
          { _pageSize: 200, sort: { _createdAt: 1 } }
        )
        const entries = logs?._list || (Array.isArray(logs) ? logs : [])
        if (entries.length) {
          console.log(`Logs (${entries.length}):`)
          entries.forEach((entry, idx) => {
            const ts = entry._createdAt || entry._timestamp || ''
            const lvl = entry._type || entry._level || ''
            const msg = entry._message ?? entry.message ?? entry
            console.log(`  ${String(idx + 1).padStart(3, '0')} [${ts}] [${lvl}]`, msg)
          })
        } else {
          console.log('(No log entries)')
        }
      } catch (err) {
        console.warn('Failed to fetch task logs:', err?.errorResult?.errorMessage || err?.message || err)
      }
      console.groupEnd()
    }

    console.log('=================================')
    setSuccessMessage('Workflow run details + per-task logs logged to console (F12)')
  }

  // ============================================================================
  // DEVELOPER EMAILS MANAGEMENT
  // ============================================================================

  const addDeveloperEmail = () => {
    if (newDeveloperEmail.trim() && emailIsValid(newDeveloperEmail.trim())) {
      const normalized = newDeveloperEmail.trim().toLowerCase()
      if (!developerEmails.includes(normalized)) {
        setDeveloperEmails([...developerEmails, normalized])
      }
      setNewDeveloperEmail('')
    }
  }

  const removeDeveloperEmail = (email) => {
    setDeveloperEmails(developerEmails.filter(e => e !== email))
  }

  const handleDeveloperEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addDeveloperEmail()
    }
  }

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Auto-populate admin email with the fixed onboarding admin address,
  // unless the current user IS that admin (in which case they are onboarding themselves).
  const ONBOARDING_ADMIN_EMAIL = 'scott.mollon@invicara.com'
  useEffect(() => {
    if (!adminEmail && currentUser?._email !== ONBOARDING_ADMIN_EMAIL) {
      setAdminEmail(ONBOARDING_ADMIN_EMAIL)
    }
  }, [currentUser])  // eslint-disable-line react-hooks/exhaustive-deps

  const currentUserEmail = currentUser?._email || ''

  useEffect(() => {
    loadPermissionProfile()
    loadOrchestrator()
    loadWorkflow()
    loadSecrets()
    loadOrganizations()
  }, [loadPermissionProfile, loadOrchestrator, loadWorkflow, loadSecrets, loadOrganizations])

  // Reload organizations when console token changes (gives platform-level visibility)
  useEffect(() => {
    if (consoleToken) {
      loadOrganizations()
    }
  }, [consoleToken, loadOrganizations])

  useEffect(() => {
    if (orchestrator) {
      loadRecentRuns()
      const interval = setInterval(() => {
        if (runningCount > 0) {
          loadRecentRuns()
        }
      }, 10000)
      return () => clearInterval(interval)
    }
  }, [orchestrator, runningCount, loadRecentRuns])

  useEffect(() => {
    if (workflow) {
      loadRecentWorkflowRuns()
      const interval = setInterval(() => {
        if (runningWorkflowCount > 0) {
          loadRecentWorkflowRuns()
        }
      }, 10000)
      return () => clearInterval(interval)
    }
  }, [workflow, runningWorkflowCount, loadRecentWorkflowRuns])

  // Load applications when organization changes — but don't clear the selected
  // application on the initial mount (hot reload), only when the org actually changes.
  const prevOrgIdRef = useRef(undefined)
  useEffect(() => {
    const isFirstRun = prevOrgIdRef.current === undefined
    const orgChanged = prevOrgIdRef.current !== selectedOrganizationId
    prevOrgIdRef.current = selectedOrganizationId

    if (selectedOrganizationId && !createNewOrganization) {
      loadApplications(selectedOrganizationId)
      if (!isFirstRun && orgChanged) {
        setSelectedApplicationId('')
        setCreateNewApplication(false)
      }
    } else {
      setApplications([])
    }
  }, [selectedOrganizationId, createNewOrganization, loadApplications])

  // Validation effect
  useEffect(() => {
    // Admin email is the org owner — only required when creating a new organization
    const needsAdminEmail = createNewOrganization

    // Check if organization is valid
    const orgValid = createNewOrganization
      ? !!organizationName.trim() && (!organizationShortName || organizationShortName.length <= 22)
      : !!selectedOrganizationId

    // Check if application is valid
    const appValid = createNewApplication
      ? !!applicationName.trim() && applicationShortName.length <= 22
      : !!selectedApplicationId

    // Check if we have at least one email
    const hasEmails = (needsAdminEmail ? emailIsValid(adminEmail) : true) || developerEmails.length > 0

    setIsReady(orgValid && appValid && hasEmails)
  }, [
    createNewOrganization, createNewApplication,
    selectedOrganizationId, selectedApplicationId,
    organizationName, organizationShortName,
    applicationName, applicationShortName,
    adminEmail, developerEmails
  ])

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const emailIsValid = (email) => {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(email)
  }

  const clearForm = () => {
    setSelectedOrganizationId('')
    setCreateNewOrganization(false)
    setOrganizationName('')
    setOrganizationShortName('')
    setNewOrganizationId('')
    setSelectedApplicationId('')
    setCreateNewApplication(false)
    setApplicationName('Twinit Academy')
    setApplicationShortName('')
    setNewApplicationId('')
    setAdminEmail('')
    setDeveloperEmails([])
    setNewDeveloperEmail('')
    setDryRun(false)
    setAddToAppOwner(false)
    setSkipRetrieveUser(false)
    setSkipEnsureOrganization(false)
    setSkipSetOrgConfig(false)
    setSkipEnsureApp(false)
    setSkipAddToAppDevs(false)
    setSkipAddToEnabledDevs(false)
    setError('')
    setSuccessMessage('')
    setLastRunResult(null)
  }

  // Invite email defaults — match developer_onboarding_shared addUsersToAppDevelopers
  // so both orchestrator and workflow runs pass explicit USER_GROUP_INVITE _params.
  const defaultInviteBodyContent =
    `<p>In the next few minutes you will be receiving a few invitations to various Twinit resources including Twinit Academy. ` +
    `I&rsquo;d like to quickly provide you a summary of all the resources that will be available to you. ` +
    `If you&rsquo;d like to schedule a call to walk through and receive an introduction to any or all of these resources please let me know. ` +
    `I would be happy to do so.</p>`

  const buildInviteParams = () => {
    const apiUrl = getApiUrl(handler)
    const inviterFromUser = [currentUser?._firstname, currentUser?._lastname]
      .filter(Boolean)
      .join(' ')
      .trim()
    return {
      inviteLink: `${apiUrl}/passportsvc/api/accounts`,
      inviterName: inviterFromUser || 'Invicara Developer Team',
      bodyContent: defaultInviteBodyContent,
    }
  }

  // Build the common runtime input shared by both execution services.
  // The orchestrator and workflow scripts accept the same field names; only
  // the script entry point and the post-run refresh differ.
  const buildRunInput = () => ({
    // Organization - use selectedOrganizationId if using existing, or newOrganizationId if creating with custom ID
    organizationId: createNewOrganization ? undefined : selectedOrganizationId,
    newOrganizationId: createNewOrganization && newOrganizationId.trim() ? newOrganizationId.trim() : undefined,
    organizationName: createNewOrganization ? organizationName.trim() : undefined,
    organizationShortName: createNewOrganization && organizationShortName.trim() ? organizationShortName.trim() : undefined,
    // Application
    applicationId: createNewApplication ? undefined : selectedApplicationId,
    newApplicationId: createNewApplication && newApplicationId.trim() ? newApplicationId.trim() : undefined,
    applicationName: createNewApplication ? applicationName.trim() : undefined,
    applicationShortName: createNewApplication ? applicationShortName.trim() : undefined,
    // Users
    adminEmail: adminEmail.trim() || undefined,
    developerEmails: developerEmails.length > 0 ? developerEmails : undefined,
    // Options
    dryRun,
    // Skip flags
    // NB: coerce derived flags to booleans. `||` returns the first truthy
    // operand verbatim, so e.g. `false || (!createNewOrganization && selectedOrganizationId)`
    // resolves to the org id string instead of `true`, and that string would
    // be sent on the wire as the skip flag value (causing downstream scripts
    // to treat the flag as truthy but render the id wherever they log it).
    skipRetrieveUser,
    skipEnsureOrganization: Boolean(skipEnsureOrganization || (!createNewOrganization && selectedOrganizationId)),
    skipSetOrgConfig: Boolean(skipSetOrgConfig || (!createNewOrganization && selectedOrganizationId)),
    skipEnsureApp: Boolean(skipEnsureApp || (!createNewApplication && selectedApplicationId)),
    skipAddToAppDevs,
    skipAddToEnabledDevs,
    // Platform Console token for privileged operations (org/app creation)
    consoleAuthToken: consoleToken || undefined,
    // Management app ID so scripts can whitelist it in newly-created apps
    managementAppId: managementApp?._id || undefined,
    // Current user — becomes the app _appOwner on newly created apps
    currentUserEmail: currentUserEmail || undefined,
    // Invite email customization (USER_GROUP_INVITE template)
    ...buildInviteParams(),
  })

  const runOnboardingOrchestrator = async () => {
    setIsRunningOrch(true)
    setError('')
    setSuccessMessage('')
    setLastRunResult(null)

    try {
      const input = {
        orchestratorUserType: 'developer_onboarding',
        ...buildRunInput()
      }

      let result = await ScriptHelper.executeScript(
        scripts.runDeveloperOnboardingOrchestrator,
        input
      )

      console.log('Run orchestrator result:', result)

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx();
        result = await orchestratorModule.runDeveloperOnboardingOrchestrator(input, {PlatformApi, IafScriptEngine}, ctx);
        console.log('Run orchestrator module result:', result)
      }

      setLastRunResult({ service: 'orchestrator', input, result })
      setSuccessMessage('Developer onboarding orchestrator started! Check Recent Runs for status.')
      await loadRecentRuns()
    } catch (err) {
      console.error('Onboarding (orchestrator) failed:', err)
      setError(err.message || 'Failed to run developer onboarding orchestrator')
    } finally {
      setIsRunningOrch(false)
    }
  }

  const runOnboardingWorkflow = async () => {
    setIsRunningWorkflow(true)
    setError('')
    setSuccessMessage('')
    setLastRunResult(null)

    try {
      const input = {
        workflowUserType: 'developer_onboarding_workflow',
        ...buildRunInput()
      }

      let result = await ScriptHelper.executeScript(
        scripts.runDeveloperOnboardingWorkflow,
        input
      )

      console.log('Run workflow result:', result)

      if (typeof result === 'string' && result.includes('not found')) {
        const ctx = getCtx()
        result = await workflowModule.runDeveloperOnboardingWorkflow(input, {PlatformApi, IafScriptEngine}, ctx)
        console.log('Run workflow module result:', result)
      }

      setLastRunResult({ service: 'workflow', input, result })
      setSuccessMessage('Developer onboarding workflow started! Check Recent Runs for status.')
      await loadRecentWorkflowRuns()
    } catch (err) {
      console.error('Onboarding (workflow) failed:', err)
      setError(err.message || 'Failed to run developer onboarding workflow')
    } finally {
      setIsRunningWorkflow(false)
    }
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  const getStatusBadge = (status) => {
    const statusMap = {
      'completed': { class: 'success', icon: 'fa-check-circle' },
      'running': { class: 'running', icon: 'fa-spinner fa-spin' },
      'pending': { class: 'pending', icon: 'fa-clock' },
      'failed': { class: 'error', icon: 'fa-times-circle' },
      'error': { class: 'error', icon: 'fa-times-circle' }
    }
    const config = statusMap[status] || { class: 'unknown', icon: 'fa-question-circle' }
    return (
      <span className={`status-badge ${config.class}`}>
        <i className={`fas ${config.icon}`}></i>
        {status}
      </span>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const timestamp = parseInt(dateString)
    if (isNaN(timestamp)) return 'Invalid Date'
    return new Date(timestamp).toLocaleString()
  }

  const getOrchestratorSteps = () => {
    if (!orchestrator?.orchsteps) return []
    return orchestrator.orchsteps
  }

  // Pagination - orchestrator runs
  const indexOfLastRun = currentPage * runsPerPage
  const indexOfFirstRun = indexOfLastRun - runsPerPage
  const currentRuns = recentRuns.slice(indexOfFirstRun, indexOfLastRun)
  const totalPages = Math.ceil(recentRuns.length / runsPerPage)

  // Pagination - workflow runs
  const indexOfLastWorkflowRun = workflowCurrentPage * runsPerPage
  const indexOfFirstWorkflowRun = indexOfLastWorkflowRun - runsPerPage
  const currentWorkflowRuns = recentWorkflowRuns.slice(indexOfFirstWorkflowRun, indexOfLastWorkflowRun)
  const totalWorkflowPages = Math.ceil(recentWorkflowRuns.length / runsPerPage)

  // Get selected organization/application names for display
  const selectedOrgName = organizations.find(o => o._id === selectedOrganizationId)?._name || ''
  const selectedAppName = applications.find(a => a._id === selectedApplicationId)?._name || ''

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="dev-onboarding">
      {/* Header */}
      <div className="dev-onboarding-header">
        <div className="header-title">
          <i className="fas fa-user-plus"></i>
          <h1>Developer Onboarding</h1>
          {envInfo.env && envInfo.env !== 'Unknown' && (
            <span className="env-label">{envInfo.env}</span>
          )}
          <button
            className="help-btn"
            onClick={() => setShowHelp(true)}
            title="How to use this page"
            style={{ marginLeft: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#c71784', fontSize: 18, lineHeight: 1 }}
          >
            <i className="fas fa-question-circle"></i>
          </button>
        </div>
        <p className="header-subtitle">
          Automate developer setup with pre-configured orchestrator workflows
        </p>
        {envInfo.host && (
          <div className="env-badge">
            <i className="fas fa-server"></i>
            <span>{envInfo.host}</span>
          </div>
        )}
        {(runningCount > 0 || runningWorkflowCount > 0) && (
          <div className="running-indicator">
            <span className="pulse-dot"></span>
            {runningCount > 0 && (
              <span>{runningCount} orchestrator{runningCount > 1 ? ' runs' : ' run'}</span>
            )}
            {runningCount > 0 && runningWorkflowCount > 0 && <span>, </span>}
            {runningWorkflowCount > 0 && (
              <span>{runningWorkflowCount} workflow{runningWorkflowCount > 1 ? ' runs' : ' run'}</span>
            )}
            <span>&nbsp;in flight</span>
          </div>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="message-banner error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="message-banner success">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage('')}>×</button>
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'onboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('onboard')}
          disabled={!orchestrator && !workflow}
          title={!orchestrator && !workflow ? 'Create the workflow first in Setup' : ''}
        >
          <i className="fas fa-rocket"></i>
          Onboard Developer
        </button>
        <button
          className={`tab ${activeTab === 'setup' ? 'active' : ''}`}
          onClick={() => setActiveTab('setup')}
        >
          <i className="fas fa-cog"></i>
          Setup
        </button>
      </div>

      {/* SETUP TAB */}
      {activeTab === 'setup' && (
        <>
          {/* Permission Profile Section — hidden (cannot run workflows/orchestrators inside a profile) */}

          {/* Orchestrator Setup Section — collapsed by default (legacy, not in use) */}
          <details className="section-card section-card-collapsible">
            <summary style={{ cursor: 'pointer', listStyle: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 style={{ margin: 0 }}>
                <i className="fas fa-cogs"></i>
                Orchestrator Setup
                <span style={{ fontSize: '0.75em', fontWeight: 'normal', color: '#888', marginLeft: 10 }}>(legacy — not in use)</span>
              </h2>
              <i className="fas fa-chevron-down" style={{ marginLeft: 'auto', color: '#888', fontSize: '0.85em' }}></i>
            </summary>
        <p className="section-description">
          The developer onboarding orchestrator automates the complete setup process for new developers.
        </p>

        {orchestratorLoading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading orchestrator...</span>
          </div>
        ) : orchestrator ? (
          <div className="orchestrator-info">
            <div className="orchestrator-status">
              <div className="status-icon success">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="status-details">
                <h3>{orchestrator._name || 'Developer Onboarding Orchestrator'}</h3>
                <p>Ready to onboard new developers</p>
              </div>
              <div className="status-meta">
                <span className="type-badge instant">
                  <i className="fas fa-bolt"></i> Instant
                </span>
                <button
                  className="btn-danger btn-sm"
                  onClick={deleteOrchestrator}
                  disabled={deletingOrchestrator}
                  title="Delete orchestrator"
                >
                  {deletingOrchestrator ? (
                    <><i className="fas fa-spinner fa-spin"></i></>
                  ) : (
                    <><i className="fas fa-trash"></i> Delete</>
                  )}
                </button>
              </div>
            </div>

            {/* Permission Profile Status */}
            <div className="profile-attachment-status">
              <h4><i className="fas fa-link"></i> Permission Profile</h4>
              {orchestrator._permissionprofileid ? (
                <div className="profile-attached">
                  <div className="profile-info">
                    <span className="status-badge warning">
                      <i className="fas fa-exclamation-triangle"></i> Profile attached (not supported)
                    </span>
                    <code className="profile-id">{orchestrator._permissionprofileid}</code>
                    <p className="warning-text">
                      Permission profiles cannot be used with orchestrators. Remove it to run with user permissions.
                    </p>
                  </div>
                  <button
                    className="btn-danger btn-sm"
                    onClick={removeOrchestratorProfile}
                    disabled={updatingProfile}
                  >
                    {updatingProfile ? (
                      <><i className="fas fa-spinner fa-spin"></i> Removing...</>
                    ) : (
                      <><i className="fas fa-unlink"></i> Remove Profile</>
                    )}
                  </button>
                </div>
              ) : (
                <div className="profile-not-attached">
                  <span className="status-badge success">
                    <i className="fas fa-check-circle"></i> No profile (runs with user permissions)
                  </span>
                </div>
              )}
            </div>

            {/* Steps Preview */}
            <div className="orchestrator-steps">
              <h4><i className="fas fa-list-ol"></i> Orchestrator Steps ({getOrchestratorSteps().length})</h4>
              <div className="steps-grid">
                {getOrchestratorSteps().map((task, idx) => (
                  <div key={idx} className="step-item">
                    <span className="step-number">{task._sequenceno || idx + 1}</span>
                    <span className="step-name">{task._name}</span>
                  </div>
                ))}
                {getOrchestratorSteps().length === 0 && (
                  <p className="no-steps">No steps configured</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="orchestrator-missing">
            <div className="missing-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3>Orchestrator Not Found</h3>
            <p>The developer onboarding orchestrator needs to be created before you can onboard developers.</p>
            <button
              className="btn-primary"
              onClick={createOrchestrator}
              disabled={creatingOrchestrator || (permissionProfileRequired && !permissionProfile)}
              title={permissionProfileRequired && !permissionProfile ? 'Create permission profile first' : ''}
            >
              {creatingOrchestrator ? (
                <><i className="fas fa-spinner fa-spin"></i> Creating...</>
              ) : (
                <><i className="fas fa-plus"></i> Create Orchestrator</>
              )}
            </button>
            {permissionProfileRequired && !permissionProfile && (
              <p className="warning-text">
                <i className="fas fa-exclamation-triangle"></i>
                Permission profile must be created first
              </p>
            )}
          </div>
        )}
      </details>

          {/* Workflow Setup Section */}
          {/* The workflow uses the
              new Twinit WorkflowSvc (POST /workflowsvc/api/v1/workflows) while
              the orchestrator uses the legacy DataSourceSvc orchestrator API. */}
          <div className="section-card">
            <h2>
              <i className="fas fa-project-diagram"></i>
              Workflow Setup
            </h2>
            <p className="section-description">
              The developer onboarding workflow runs the same onboarding steps via
              the Twinit Workflow Service, with task-level chaining and per-task
              retry/timeout policies.
            </p>

            {workflowLoading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading workflow...</span>
              </div>
            ) : workflow ? (
              <div className="orchestrator-info">
                <div className="orchestrator-status">
                  <div className="status-icon success">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div className="status-details">
                    <h3>{workflow._name || 'Developer Onboarding Workflow'}</h3>
                    <p>Ready to onboard new developers via WorkflowSvc</p>
                  </div>
                  <div className="status-meta">
                    <span className="type-badge instant">
                      <i className="fas fa-project-diagram"></i> Workflow
                    </span>
                    <button
                      className="btn-danger btn-sm"
                      onClick={deleteWorkflow}
                      disabled={deletingWorkflow}
                      title="Delete workflow"
                    >
                      {deletingWorkflow ? (
                        <><i className="fas fa-spinner fa-spin"></i></>
                      ) : (
                        <><i className="fas fa-trash"></i> Delete</>
                      )}
                    </button>
                  </div>
                </div>

                {/* Permission Profile Status (mirror of orchestrator) */}
                <div className="profile-attachment-status">
                  <h4><i className="fas fa-link"></i> Permission Profile</h4>
                  {workflow._permissionProfileId ? (
                    <div className="profile-attached">
                      <div className="profile-info">
                        <span className="status-badge warning">
                          <i className="fas fa-exclamation-triangle"></i> Profile attached (not supported)
                        </span>
                        <code className="profile-id">{workflow._permissionProfileId}</code>
                        <p className="warning-text">
                          Permission profiles cannot be used with workflows. Remove it to run with user permissions.
                        </p>
                      </div>
                      <button
                        className="btn-danger btn-sm"
                        onClick={removeWorkflowProfile}
                        disabled={updatingWorkflowProfile}
                      >
                        {updatingWorkflowProfile ? (
                          <><i className="fas fa-spinner fa-spin"></i> Removing...</>
                        ) : (
                          <><i className="fas fa-unlink"></i> Remove Profile</>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="profile-not-attached">
                      <span className="status-badge success">
                        <i className="fas fa-check-circle"></i> No profile (runs with user permissions)
                      </span>
                    </div>
                  )}
                </div>

                {/* Task Defs Preview */}
                <div className="orchestrator-steps">
                  <h4><i className="fas fa-list-ol"></i> Workflow Tasks ({(workflow._taskDefs || []).length})</h4>
                  <div className="steps-grid">
                    {(workflow._taskDefs || []).map((task, idx) => (
                      <div key={idx} className="step-item">
                        <span className="step-number">{task._sequenceno || idx + 1}</span>
                        <span className="step-name">{task._name}</span>
                      </div>
                    ))}
                    {(!workflow._taskDefs || workflow._taskDefs.length === 0) && (
                      <p className="no-steps">No tasks configured</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="orchestrator-missing">
                <div className="missing-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h3>Workflow Not Found</h3>
                <p>The developer onboarding workflow needs to be created before you can onboard developers via the Workflow Service.</p>
                <button
                  className="btn-primary"
                  onClick={createWorkflow}
                  disabled={creatingWorkflow || (permissionProfileRequired && !permissionProfile)}
                  title={permissionProfileRequired && !permissionProfile ? 'Create permission profile first' : ''}
                >
                  {creatingWorkflow ? (
                    <><i className="fas fa-spinner fa-spin"></i> Creating...</>
                  ) : (
                    <><i className="fas fa-plus"></i> Create Workflow</>
                  )}
                </button>
                {permissionProfileRequired && !permissionProfile && (
                  <p className="warning-text">
                    <i className="fas fa-exclamation-triangle"></i>
                    Permission profile must be created first
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Advanced / Legacy: collapsed Secrets panel.
              The orchestrator's two-tier auth fallback uses these credentials;
              the new workflow path does not. Kept here for QA/legacy support. */}
          <div className="section-card">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowLegacySecrets(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: 'space-between' }}
              aria-expanded={showLegacySecrets}
            >
              <span>
                <i className={`fas fa-chevron-${showLegacySecrets ? 'down' : 'right'}`}></i>
                {' '}Advanced / Legacy: API Credentials (Secrets)
              </span>
              <span style={{ opacity: 0.6, fontSize: '0.8125rem' }}>
                Only needed for orchestrator fallback auth
              </span>
            </button>

            {showLegacySecrets && (
              <div style={{ marginTop: '1rem' }}>
                <p className="section-description">
                  Configure OAuth credentials for the orchestrator's two-tier auth fallback.
                  These are used when the orchestrator's permission profile lacks a needed permission.
                  The new Workflow Service path does not use these.
                </p>

                {secretsLoading ? (
                  <div className="loading-state">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading secrets...</span>
                  </div>
                ) : (
                  <div className="secrets-form">
                    <div className="secrets-status">
                      <h3 className="secrets-section-title">
                        <i className="fas fa-flask"></i> SANDBOX Credentials
                      </h3>
                      <div className="secret-status-item">
                        <div className="secret-status-label">
                          <i className="fas fa-lock"></i>
                          <span>SANDBOX_CLIENT_SECRET</span>
                        </div>
                        {existingSandboxSecret ? (
                          <div className="secret-status-value">
                            <span className="status-badge success">
                              <i className="fas fa-check-circle"></i> Configured
                            </span>
                            <button
                              className="btn-secondary btn-sm"
                              onClick={() => deleteSecret(SECRET_NAMES.SANDBOX)}
                            >
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </div>
                        ) : (
                          <span className="status-badge error">
                            <i className="fas fa-times-circle"></i> Not configured
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="secrets-status">
                      <h3 className="secrets-section-title">
                        <i className="fas fa-server"></i> PROD Credentials
                      </h3>
                      <div className="secret-status-item">
                        <div className="secret-status-label">
                          <i className="fas fa-lock"></i>
                          <span>PROD_CLIENT_SECRET</span>
                        </div>
                        {existingProdSecret ? (
                          <div className="secret-status-value">
                            <span className="status-badge success">
                              <i className="fas fa-check-circle"></i> Configured
                            </span>
                            <button
                              className="btn-secondary btn-sm"
                              onClick={() => deleteSecret(SECRET_NAMES.PROD)}
                            >
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </div>
                        ) : (
                          <span className="status-badge error">
                            <i className="fas fa-times-circle"></i> Not configured
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="secrets-update-form">
                      <h3>
                        <i className="fas fa-edit"></i>
                        Update Client Secrets
                      </h3>
                      <p className="form-hint">
                        Leave fields empty to keep existing values. Only non-empty fields will be updated.
                      </p>

                      <h4 className="form-section-title">
                        <i className="fas fa-flask"></i> SANDBOX Client Secret
                      </h4>
                      <div className="form-group">
                        <label>SANDBOX_CLIENT_SECRET</label>
                        <input
                          type="password"
                          value={sandboxClientSecret}
                          onChange={(e) => setSandboxClientSecret(e.target.value)}
                          placeholder={existingSandboxSecret ? 'Enter new value to update' : 'Enter client secret'}
                          disabled={savingSecrets}
                        />
                      </div>

                      <h4 className="form-section-title">
                        <i className="fas fa-server"></i> PROD Client Secret
                      </h4>
                      <div className="form-group">
                        <label>PROD_CLIENT_SECRET</label>
                        <input
                          type="password"
                          value={prodClientSecret}
                          onChange={(e) => setProdClientSecret(e.target.value)}
                          placeholder={existingProdSecret ? 'Enter new value to update' : 'Enter client secret'}
                          disabled={savingSecrets}
                        />
                      </div>

                      <div className="form-actions">
                        <button
                          className="btn-primary"
                          onClick={saveSecrets}
                          disabled={savingSecrets || (!sandboxClientSecret.trim() && !prodClientSecret.trim())}
                        >
                          {savingSecrets ? (
                            <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                          ) : (
                            <><i className="fas fa-save"></i> Save Secrets</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* ONBOARD TAB - shown when at least one execution service exists */}
      {activeTab === 'onboard' && (orchestrator || workflow) && (
        <>
          {/* Onboarding Form Section */}
          <div className="section-card">
            <h2>
            <i className="fas fa-rocket"></i>
            Onboard New Developer
          </h2>
            <p className="section-description">
            Configure the organization, application, and users to onboard.
          </p>

            {/* Platform Console App Auth - at the top of the flow */}
            <div className="console-auth-section" style={{ marginBottom: '20px', padding: '12px 16px', background: isTokenExpiring ? '#fff8e1' : hasConsoleToken ? '#e8f5e9' : '#fff3e0', borderRadius: '6px', border: `1px solid ${isTokenExpiring ? '#ffe082' : hasConsoleToken ? '#a5d6a7' : '#ffcc80'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <i className={`fas ${isTokenExpiring ? 'fa-exclamation-triangle' : hasConsoleToken ? 'fa-check-circle' : 'fa-info-circle'}`} style={{ color: isTokenExpiring ? '#f57f17' : hasConsoleToken ? '#2e7d32' : '#e65100', fontSize: '1.2em' }}></i>
                <div style={{ flex: 1 }}>
                  <strong>Platform Console Authorization</strong>
                  <div style={{ fontSize: '0.85em', color: '#555', marginTop: '2px' }}>
                    {isTokenExpiring
                      ? '⚠ Console token is about to expire. Re-authenticate before running the workflow to avoid failures.'
                      : hasConsoleToken
                        ? 'Console token active. Organizations and applications are listed with full platform visibility.'
                        : 'Sign in with the Platform Console to see all organizations/applications and enable creation of new ones.'}
                  </div>
                  {consoleAuthError && <div style={{ color: '#c62828', fontSize: '0.85em', marginTop: '4px' }}>{consoleAuthError}</div>}
                </div>
                {hasConsoleToken ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {isTokenExpiring && (
                      <button
                        className="btn-primary"
                        onClick={authenticateConsole}
                        disabled={isConsoleAuthenticating}
                        style={{ fontSize: '0.85em' }}
                        title="Token is expiring — re-authenticate"
                      >
                        <i className="fas fa-key"></i> Re-authenticate
                      </button>
                    )}
                    <button
                      className="btn-secondary"
                      onClick={() => { console.log('[Console Token]', consoleToken); }}
                      style={{ fontSize: '0.85em' }}
                      title="Print console token to browser console"
                    >
                      <i className="fas fa-terminal"></i> Log Token
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => { clearConsoleToken(); loadOrganizations(); }}
                      style={{ fontSize: '0.85em' }}
                    >
                      <i className="fas fa-sign-out-alt"></i> Clear Token
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={authenticateConsole}
                    disabled={isConsoleAuthenticating}
                    style={{ fontSize: '0.85em' }}
                  >
                    {isConsoleAuthenticating
                      ? <><i className="fas fa-spinner fa-spin"></i> Authenticating...</>
                      : <><i className="fas fa-key"></i> Sign in with Console</>}
                  </button>
                )}
              </div>
            </div>

            <div className="onboarding-form">
              {/* Organization Selection */}
              <div className="form-section">
                <h3><i className="fas fa-building"></i> Organization</h3>

                <div className="form-group">
                  <label>
                    Select Organization
                  </label>
                  <div className="select-with-action">
                    <select
                      value={createNewOrganization ? '__create__' : selectedOrganizationId}
                      onChange={(e) => {
                        if (e.target.value === '__create__') {
                          setCreateNewOrganization(true)
                          setSelectedOrganizationId('')
                        } else {
                          setCreateNewOrganization(false)
                          setSelectedOrganizationId(e.target.value)
                        }
                      }}
                      disabled={isRunning || loadingOrganizations}
                    >
                      <option value="">-- Select an organization --</option>
                      {organizations.map(org => (
                        <option key={org._id} value={org._id}>
                          {org._name} ({org._shortName})
                        </option>
                      ))}
                      <option value="__create__">➕ Create New Organization</option>
                    </select>
                    <button
                      className="btn-icon"
                      onClick={loadOrganizations}
                      disabled={loadingOrganizations}
                      title="Refresh organizations"
                    >
                      <i className={`fas fa-sync-alt ${loadingOrganizations ? 'fa-spin' : ''}`}></i>
                    </button>
                  </div>
                </div>

                {createNewOrganization && (
                  <div className="form-grid nested">
                    <div className="form-group">
                      <label>
                        Organization Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={organizationName}
                        placeholder="Acme Corp"
                        disabled={isRunning}
                        onChange={(e) => setOrganizationName(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Organization Short Name
                        <span className="form-hint-inline"> (optional, max 22 chars)</span>
                      </label>
                      <input
                        type="text"
                        value={organizationShortName}
                        placeholder="acme_corp"
                        disabled={isRunning}
                        maxLength={22}
                        onChange={(e) => setOrganizationShortName(e.target.value)}
                      />
                      {organizationShortName && organizationShortName.length > 22 && (
                        <span className="form-error">Must be 22 characters or less</span>
                      )}
                    </div>

                    <div className="form-group full-width">
                      <label>
                        Organization ID
                        <span className="form-hint-inline"> (optional - specify custom ID for creation)</span>
                      </label>
                      <input
                        type="text"
                        value={newOrganizationId}
                        placeholder="Leave empty for auto-generated ID"
                        disabled={isRunning}
                        onChange={(e) => setNewOrganizationId(e.target.value)}
                      />
                      <span className="form-hint">If provided, the organization will be created with this specific ID</span>
                    </div>

                    {/* Org Owner Email — required when creating a new organization */}
                    <div className="form-group full-width">
                      <label>
                        Org Owner Email <span className="required">*</span>
                      </label>
                      <div className="email-input-container">
                        <input
                          type="email"
                          value={adminEmail}
                          placeholder="admin@example.com"
                          disabled={isRunning}
                          onChange={(e) => setAdminEmail(e.target.value)}
                        />
                        {currentUserEmail && adminEmail.toLowerCase() !== currentUserEmail.toLowerCase() && (
                          <button
                            className="btn-secondary btn-sm"
                            title={`Use my email: ${currentUserEmail}`}
                            disabled={isRunning}
                            onClick={() => setAdminEmail(currentUserEmail)}
                          >
                            Use my email
                          </button>
                        )}
                      </div>
                      <span className="form-hint">This user will be the owner of the new organization</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Application Selection */}
              <div className="form-section">
                <h3><i className="fas fa-cube"></i> Application</h3>

                <div className="form-group">
                  <label>
                    Select Application
                  </label>
                  <div className="select-with-action">
                    <select
                      value={createNewApplication ? '__create__' : selectedApplicationId}
                      onChange={(e) => {
                        if (e.target.value === '__create__') {
                          setCreateNewApplication(true)
                          setSelectedApplicationId('')
                        } else {
                          setCreateNewApplication(false)
                          setSelectedApplicationId(e.target.value)
                        }
                      }}
                      disabled={isRunning || loadingApplications || (!selectedOrganizationId && !createNewOrganization)}
                    >
                      <option value="">-- Select an application --</option>
                      {applications.map(app => (
                        <option key={app._id} value={app._id}>
                          {app._name} ({app._shortName})
                        </option>
                      ))}
                      <option value="__create__">➕ Create New Application</option>
                    </select>
                    {(selectedOrganizationId && !createNewOrganization) && (
                      <button
                        className="btn-icon"
                        onClick={() => loadApplications(selectedOrganizationId)}
                        disabled={loadingApplications}
                        title="Refresh applications"
                      >
                        <i className={`fas fa-sync-alt ${loadingApplications ? 'fa-spin' : ''}`}></i>
                      </button>
                    )}
                  </div>
                  {(!selectedOrganizationId && !createNewOrganization) && (
                    <span className="form-hint">Select an organization first</span>
                  )}
                </div>

                {createNewApplication && (
                  <div className="form-grid nested">
                    <div className="form-group">
                      <label>
                        Application Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={applicationName}
                        placeholder="Twinit Academy"
                        disabled={isRunning}
                        onChange={(e) => setApplicationName(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Application Short Name <span className="required">*</span>
                        <span className="form-hint-inline"> (max 22 chars)</span>
                      </label>
                      <input
                        type="text"
                        value={applicationShortName}
                        placeholder="academy_<org> (auto-derived if empty)"
                        disabled={isRunning}
                        maxLength={22}
                        onChange={(e) => setApplicationShortName(e.target.value)}
                      />
                      {applicationShortName.length > 22 && (
                        <span className="form-error">Must be 22 characters or less</span>
                      )}
                    </div>

                    <div className="form-group full-width">
                      <label>
                        Application ID
                        <span className="form-hint-inline"> (optional - specify custom ID for creation)</span>
                      </label>
                      <input
                        type="text"
                        value={newApplicationId}
                        placeholder="Leave empty for auto-generated ID"
                        disabled={isRunning}
                        onChange={(e) => setNewApplicationId(e.target.value)}
                      />
                      <span className="form-hint">If provided, the application will be created with this specific ID</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Users Section */}
              <div className="form-section">
                <h3><i className="fas fa-users"></i> Users to Onboard</h3>

                {/* Developer Emails - multi-select */}
                <div className="form-group">
                  <label>
                    Developer Emails
                    {!(createNewOrganization || createNewApplication) && <span className="required">*</span>}
                  </label>
                  <div className="email-input-container">
                    <input
                      type="email"
                      value={newDeveloperEmail}
                      placeholder="developer@example.com"
                      disabled={isRunning}
                      onChange={(e) => setNewDeveloperEmail(e.target.value)}
                      onKeyDown={handleDeveloperEmailKeyDown}
                    />
                    <button
                      className="btn-secondary btn-sm"
                      onClick={addDeveloperEmail}
                      disabled={isRunning || !newDeveloperEmail.trim() || !emailIsValid(newDeveloperEmail)}
                    >
                      <i className="fas fa-plus"></i> Add
                    </button>
                  </div>
                  {newDeveloperEmail.trim()
                    ? <span className="form-hint" style={{color: '#c17a00', fontWeight: 500}}>⚠ Press Enter or click Add to confirm this email before running</span>
                    : <span className="form-hint">Press Enter or click Add to add multiple developers</span>
                  }

                  {developerEmails.length > 0 && (
                    <div className="email-tags">
                      {developerEmails.map((email, idx) => (
                        <span key={idx} className="email-tag">
                          {email}
                          <button
                            className="remove-tag"
                            onClick={() => removeDeveloperEmail(email)}
                            disabled={isRunning}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Options */}
              <div className="form-section">
                <h3><i className="fas fa-sliders-h"></i> Options</h3>

                <div className="form-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={dryRun}
                      disabled={isRunning}
                      onChange={(e) => setDryRun(e.target.checked)}
                    />
                    <span className="checkbox-content">
                      <strong>Dry Run</strong>
                      <small>Test the workflow without creating actual resources</small>
                    </span>
                  </label>

                </div>
              </div>

              {/* Skip Flags */}
              <div className="form-section collapsible">
                <h3><i className="fas fa-forward"></i> Skip Steps (Advanced)</h3>
                <p className="section-hint">Selectively skip steps if they are not needed for your use case.</p>

                <div className="form-options skip-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={skipRetrieveUser}
                      disabled={isRunning}
                      onChange={(e) => setSkipRetrieveUser(e.target.checked)}
                    />
                    <span className="checkbox-content">
                      <strong>Skip Retrieve User</strong>
                      <small>Skip user creation/retrieval step</small>
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={skipEnsureOrganization || (!createNewOrganization && !!selectedOrganizationId)}
                      disabled={isRunning || (!createNewOrganization && !!selectedOrganizationId)}
                      onChange={(e) => setSkipEnsureOrganization(e.target.checked)}
                    />
                    <span className="checkbox-content">
                      <strong>Skip Ensure Organization</strong>
                      <small>{(!createNewOrganization && selectedOrganizationId) ? 'Auto-skipped — existing org selected' : 'Skip organization creation step'}</small>
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={skipSetOrgConfig || (!createNewOrganization && !!selectedOrganizationId)}
                      disabled={isRunning}
                      onChange={(e) => setSkipSetOrgConfig(e.target.checked)}
                    />
                    <span className="checkbox-content">
                      <strong>Skip Set Org Config</strong>
                      <small>{(!createNewOrganization && selectedOrganizationId) ? 'Auto-skipped — existing org selected' : 'Skip organization configuration step'}</small>
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={skipEnsureApp || (!createNewApplication && !!selectedApplicationId)}
                      disabled={isRunning || (!createNewApplication && !!selectedApplicationId)}
                      onChange={(e) => setSkipEnsureApp(e.target.checked)}
                    />
                    <span className="checkbox-content">
                      <strong>Skip Ensure App</strong>
                      <small>{(!createNewApplication && selectedApplicationId) ? 'Auto-skipped — existing app selected' : 'Skip application creation step'}</small>
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={skipAddToAppDevs}
                      disabled={isRunning}
                      onChange={(e) => setSkipAddToAppDevs(e.target.checked)}
                    />
                    <span className="checkbox-content">
                      <strong>Skip Add to App Devs</strong>
                      <small>Skip adding users to Application Developer group</small>
                    </span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={skipAddToEnabledDevs}
                      disabled={isRunning}
                      onChange={(e) => setSkipAddToEnabledDevs(e.target.checked)}
                    />
                    <span className="checkbox-content">
                      <strong>Skip Add to Enabled Devs</strong>
                      <small>Skip adding users to Enabled Developers group (useful in PROD)</small>
                    </span>
                  </label>
                </div>
              </div>

              {/* Summary */}
              <div className="form-summary">
                <h4><i className="fas fa-clipboard-list"></i> Summary</h4>
                <ul>
                  <li>
                    <strong>Organization:</strong> {createNewOrganization ? `Create "${organizationName || '(not specified)'}"` : (selectedOrgName || '(not selected)')}
                  </li>
                  <li>
                    <strong>Application:</strong> {createNewApplication ? `Create "${applicationName || '(not specified)'}"` : (selectedAppName || '(not selected)')}
                  </li>
                  <li>
                    <strong>Users:</strong> {[adminEmail, ...developerEmails].filter(Boolean).length || 0} email(s)
                  </li>
                </ul>
              </div>

              <div className="form-actions">
                <button
                  className="btn-secondary"
                  onClick={clearForm}
                  disabled={isRunning}
                >
                  <i className="fas fa-eraser"></i>
                  Clear
                </button>
                <button
                  className="btn-primary"
                  onClick={runOnboardingOrchestrator}
                  disabled={!isReady || isRunning || !orchestrator}
                  title={!orchestrator ? 'Create the orchestrator in Setup to enable this option' : 'Run onboarding via the legacy DataSource Orchestrator service'}
                >
                  {isRunningOrch ? (
                    <><i className="fas fa-spinner fa-spin"></i> Running orchestrator...</>
                  ) : (
                    <><i className="fas fa-cogs"></i> Run via Orchestrator</>
                  )}
                </button>
                <button
                  className="btn-primary"
                  onClick={runOnboardingWorkflow}
                  disabled={!isReady || isRunning || !workflow}
                  title={!workflow ? 'Create the workflow in Setup to enable this option' : 'Run onboarding via the new Twinit Workflow Service'}
                >
                  {isRunningWorkflow ? (
                    <><i className="fas fa-spinner fa-spin"></i> Running workflow...</>
                  ) : (
                    <><i className="fas fa-project-diagram"></i> Run via Workflow</>
                  )}
                </button>
              </div>
          </div>
          </div>
        </>
      )}

      {/* Last Run Result */}
      {lastRunResult && (
        <div className="section-card">
          <h2>
            <i className="fas fa-terminal"></i>
            Last Run Result
          </h2>
          <div className="result-content">
            <div className="result-block">
              <div className="result-header">
                <span>Input</span>
                <button className="btn-copy" onClick={() => copyToClipboard(JSON.stringify(lastRunResult.input, null, 2), 'input')}>
                  <i className="fas fa-copy"></i>
                </button>
              </div>
              <pre>{JSON.stringify(
                lastRunResult.input?.consoleAuthToken
                  ? { ...lastRunResult.input, consoleAuthToken: '[REDACTED]' }
                  : lastRunResult.input,
                null, 2
              )}</pre>
            </div>
            <div className="result-block">
              <div className="result-header">
                <span>Result</span>
                <button className="btn-copy" onClick={() => copyToClipboard(JSON.stringify(lastRunResult.result, null, 2), 'result')}>
                  <i className="fas fa-copy"></i>
                </button>
              </div>
              <pre>{JSON.stringify(lastRunResult.result, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Recent Runs Section */}
      {orchestrator && (
        <div className="section-card">
          <div className="section-header">
            <h2>
              <i className="fas fa-history"></i>
              Recent Runs ({recentRuns.length})
            </h2>
            <button
              className="btn-secondary"
              onClick={loadRecentRuns}
              disabled={loadingRuns}
            >
              <i className={`fas fa-sync-alt ${loadingRuns ? 'fa-spin' : ''}`}></i>
              Refresh
            </button>
          </div>

          {loadingRuns && recentRuns.length === 0 ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading recent runs...</span>
            </div>
          ) : recentRuns.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <p>No orchestrator runs found</p>
              <p className="hint">Start your first onboarding above</p>
            </div>
          ) : (
            <>
              <div className="runs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Steps</th>
                      <th>Started</th>
                      <th>Duration</th>
                      <th>Organization</th>
                      <th>Org Owner</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRuns.map((run, idx) => {
                      const params = JSON.parse(run.orchrunsteps.find(s=>s._sequenceno==1)?._actualparams || "{}");

                      let duration = '-'
                      if (run._updatedat && run._createdat) {
                        const updatedMs = parseInt(run._updatedat)
                        const createdMs = parseInt(run._createdat)
                        if (!isNaN(updatedMs) && !isNaN(createdMs)) {
                          const durationSec = Math.round((updatedMs - createdMs) / 1000)
                          duration = `${durationSec}s`
                        }
                      }

                      return (
                        <tr key={run._id || idx}>
                          <td>{getStatusBadge(run._status)}</td>
                          <td className="steps-summary">{getStepsSummary(run)}</td>
                          <td className="date-cell">{formatDate(run._createdat)}</td>
                          <td>{duration}</td>
                          <td>{params.organizationName || '-'}</td>
                          <td>{params.adminEmail || params.userEmail || '-'}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon"
                                onClick={() => viewRunDetails(run)}
                                title="Log details to console"
                              >
                                <i className="fas fa-terminal"></i>
                              </button>
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => deleteOrchestratorRun(run.id)}
                                title="Delete run"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous
                  </button>
                  <span className="page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="btn-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Recent Workflow Runs Section - parallels the orchestrator Recent Runs
          table above. Run records come from IafWorkflowSvc.getWorkflows filtered
          by _defId. Note the field naming difference vs orchestrator runs:
          workflow runs use camelCase _createdAt/_updatedAt and uppercase
          status values (QUEUED/RUNNING/COMPLETED/FAILED). */}
      {workflow && (
        <div className="section-card">
          <div className="section-header">
            <h2>
              <i className="fas fa-history"></i>
              Recent Workflow Runs ({recentWorkflowRuns.length})
            </h2>
            <button
              className="btn-secondary"
              onClick={loadRecentWorkflowRuns}
              disabled={loadingWorkflowRuns}
            >
              <i className={`fas fa-sync-alt ${loadingWorkflowRuns ? 'fa-spin' : ''}`}></i>
              Refresh
            </button>
          </div>

          {loadingWorkflowRuns && recentWorkflowRuns.length === 0 ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading recent workflow runs...</span>
            </div>
          ) : recentWorkflowRuns.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <p>No workflow runs found</p>
              <p className="hint">Start your first onboarding via the Workflow Service above</p>
            </div>
          ) : (
            <>
              <div className="runs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Tasks</th>
                      <th>Started</th>
                      <th>Duration</th>
                      <th>Organization</th>
                      <th>Org Owner</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentWorkflowRuns.map((run, idx) => {
                      // The workflow run carries its resolved input under
                      // _inputParams (the same key we wrap things in when
                      // calling IafWorkflowSvc.runWorkflow).
                      const params = run._inputParams || {}

                      const createdMs = run._createdAt ? new Date(run._createdAt).getTime() : NaN
                      const updatedMs = run._updatedAt ? new Date(run._updatedAt).getTime() : NaN
                      let duration = '-'
                      if (!isNaN(createdMs) && !isNaN(updatedMs)) {
                        duration = `${Math.round((updatedMs - createdMs) / 1000)}s`
                      }

                      const taskCount = (run._tasks || []).length
                      const completed = (run._tasks || []).filter(t => (t._status || '').toUpperCase() === 'COMPLETED').length
                      const taskSummary = taskCount > 0 ? `${completed}/${taskCount}` : '-'

                      return (
                        <tr key={run._id || idx}>
                          <td>{getStatusBadge((run._status || '').toLowerCase())}</td>
                          <td className="steps-summary">{taskSummary}</td>
                          <td className="date-cell">{run._createdAt ? new Date(run._createdAt).toLocaleString() : '-'}</td>
                          <td>{duration}</td>
                          <td>{params.organizationName || '-'}</td>
                          <td>{params.adminEmail || '-'}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon"
                                onClick={() => viewWorkflowRunDetails(run)}
                                title="Log details to console"
                              >
                                <i className="fas fa-terminal"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {totalWorkflowPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn-secondary"
                    disabled={workflowCurrentPage === 1}
                    onClick={() => setWorkflowCurrentPage(workflowCurrentPage - 1)}
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous
                  </button>
                  <span className="page-info">
                    Page {workflowCurrentPage} of {totalWorkflowPages}
                  </span>
                  <button
                    className="btn-secondary"
                    disabled={workflowCurrentPage === totalWorkflowPages}
                    onClick={() => setWorkflowCurrentPage(workflowCurrentPage + 1)}
                  >
                    Next
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Help Modal */}
      {showHelp && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowHelp(false)}
        >
          <div
            style={{ background: '#1e1e2e', borderRadius: 16, maxWidth: 780, width: '95%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', padding: '32px 36px', position: 'relative', color: '#e0e0e0', fontFamily: 'Helvetica, Arial, sans-serif' }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowHelp(false)}
              style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#aaa', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
              title="Close"
            >
              <i className="fas fa-times"></i>
            </button>

            <h2 style={{ margin: '0 0 6px', color: '#fff', fontSize: 20 }}>
              <i className="fas fa-user-plus" style={{ color: '#c71784', marginRight: 10 }}></i>
              Developer Onboarding — How it works
            </h2>
            <p style={{ margin: '0 0 24px', color: '#999', fontSize: 14 }}>
              This page runs the Twinit Workflow Service to set up everything a new developer needs:
              a Passport organisation, a training application, notification templates, and user group memberships — all in one click.
            </p>

            {/* Annotated UI screenshot */}
            <div style={{ position: 'relative', marginBottom: 24 }}>
              <img
                src="/assets/onboarding-help.png"
                alt="Annotated Developer Onboarding UI with numbered callouts"
                onClick={() => setShowHelpImageFullscreen(true)}
                style={{ width: '100%', borderRadius: 10, display: 'block', cursor: 'zoom-in' }}
              />
              <button
                onClick={() => setShowHelpImageFullscreen(true)}
                title="View fullscreen"
                style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(0,0,0,0.6)', border: '1px solid #555', borderRadius: 6, color: '#fff', fontSize: 12, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <i className="fas fa-expand"></i> Fullscreen
              </button>
            </div>

            {/* Fullscreen image overlay */}
            {showHelpImageFullscreen && (
              <div
                onClick={() => setShowHelpImageFullscreen(false)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', cursor: 'zoom-out', padding: '40px 0' }}
              >
                <img
                  src="/assets/onboarding-help.png"
                  alt="Annotated Developer Onboarding UI — fullscreen"
                  style={{ width: '95vw', borderRadius: 10, boxShadow: '0 8px 60px rgba(0,0,0,0.8)' }}
                  onClick={e => e.stopPropagation()}
                />
                <button
                  onClick={() => setShowHelpImageFullscreen(false)}
                  style={{ position: 'fixed', top: 20, right: 24, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1 }}
                  title="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}

            <ol style={{ margin: 0, padding: '0 0 0 20px', lineHeight: 1.9, fontSize: 14, color: '#ccc' }}>
              <li>
                <strong style={{ color: '#c71784' }}>Platform Console Authorization</strong> — Sign in with the Platform Console App to obtain a privileged token. This is required for creating organisations and applications, and for managing App Owner group membership. The token auto-expires after ~1 hour; a warning appears when less than 10 minutes remain. Use <em>Log Token</em> to inspect it in the browser console, or <em>Clear Token</em> to sign out.
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Organisation</strong> — Select an existing Passport organisation from the dropdown, or choose <em>Create New Organisation</em> to create one during the workflow. When creating, fill in:
                <ul style={{ marginTop: 4, lineHeight: 1.7 }}>
                  <li><em>Organisation Name</em> — the display name (e.g. <code>org6_test_domi</code>)</li>
                  <li><em>Organisation Short Name</em> — optional slug, max 22 chars</li>
                  <li><em>Organisation ID</em> — optional custom ID; leave blank for auto-generated</li>
                  <li><em>Org Owner Email</em> — the user who will be the Passport-level org owner. They will also be invited to the Application Owner group of any new app created in this run.</li>
                </ul>
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Application</strong> — Select an existing training application, or choose <em>Create New Application</em>. When creating:
                <ul style={{ marginTop: 4, lineHeight: 1.7 }}>
                  <li>Default name is <em>Twinit Academy</em>, short name auto-derived as <code>academy_&lt;org slug&gt;</code></li>
                  <li>The <strong>current user</strong> (you) is always set as the platform-level <code>_appOwner</code> — this is required to manage templates and invite users in subsequent steps</li>
                  <li>The Org Owner Email is separately invited to the <em>Application Owner</em> user group after creation</li>
                </ul>
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Users to Onboard</strong> — Enter one or more developer email addresses and press <em>Enter</em> or click <em>Add</em> to confirm each one. Confirmed emails appear as tags. Developers will be invited to the <em>Application Developer</em> group and will receive the onboarding email with Academy resources. <strong>⚠ If text is in the input but not confirmed, it will not be included in the run.</strong>
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Options</strong> — <em>Dry Run</em> simulates all steps without making any real changes to Passport or sending any emails. Use this to verify your inputs before running for real.
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Skip Steps (Advanced)</strong> — Tick individual steps to skip them. Auto-skipped when a selection is already made (e.g. an existing org or app is chosen). Useful when re-running for additional developers without recreating the org or app. Steps: <em>Skip Retrieve User</em>, <em>Skip Set Org Config</em>, <em>Skip Ensure Org</em>, <em>Skip Ensure App</em>, <em>Skip Add to App Devs</em>, <em>Skip Add to Enabled Devs</em>.
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Summary</strong> — A live preview of the run inputs shown before you execute: Organisation, Application, and the number of email(s) to onboard.
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Run via Workflow</strong> — Executes all steps via the Twinit Workflow Service. Each step runs independently and its status is tracked. Use <em>Clear</em> to reset the form. The <em>Run via Orchestrator</em> button is a legacy path (not recommended).
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Last Run Result</strong> — Shows the raw input params and script output of the most recently completed workflow run, useful for debugging individual step results.
              </li>
              <li>
                <strong style={{ color: '#c71784' }}>Recent Workflow Runs</strong> — Shows the last runs with status, duration, organisation, and org owner. Click the <i className="fas fa-external-link-alt" style={{ fontSize: 11 }}></i> icon to open full run details in a new tab.
              </li>
            </ol>

            <div style={{ marginTop: 24, padding: '14px 18px', background: '#12122a', borderRadius: 10, borderLeft: '3px solid #c71784', fontSize: 13, color: '#aaa', lineHeight: 1.7 }}>
              <strong style={{ color: '#fff' }}>Typical first-time flow:</strong>
              <br/>
              1. Sign in with Console &rarr; 2. Choose <em>Create New Organisation</em>, fill in name &amp; org owner email &rarr; 3. Leave Application as <em>Create New Application</em> &rarr; 4. Add developer email(s) and press Enter &rarr; 5. Click <strong>Run via Workflow</strong>.
              <br/><br/>
              <strong style={{ color: '#fff' }}>Adding more developers to an existing org/app:</strong>
              <br/>
              Select the existing org and app &rarr; add new email(s) &rarr; tick <em>Skip Ensure Organisation</em>, <em>Skip Set Org Config</em>, and <em>Skip Ensure App</em> &rarr; run again.
            </div>

            <div style={{ textAlign: 'right', marginTop: 20 }}>
              <button
                onClick={() => setShowHelp(false)}
                style={{ background: '#c71784', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontWeight: 'bold', fontSize: 14 }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default DeveloperOnboardingView
